import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  SourcesParams,
  useCpyCopyMutation,
  useCpySourcesQuery,
} from "../api/cpy";
import { CpyName } from "../types/entity-names";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

export type CpySelectProps = {
  value: string | undefined;
  onValueReset?: () => void;
  destinationId: UUID;
  destinationEntityName: CpyName;
  sourceEntityName: CpyName;
  sourcesParams: SourcesParams;
  placeholder?: string;
  warning?: string;
  disabled?: boolean;
  /** Marks the select as invalid. */
  invalid?: boolean;
  /** Called when a source is selected, before it is copied. */
  onSelect?: (sourceId: string) => void;
};

/*
 * CpySelect is a simple select component to select from
 * a list of sources and trigger a copy operation into
 * the destination.
 *
 * This may be very useful when an entity references another
 * entity and the selection of the other entity should trigger
 * a copy operation into the original entity.
 *
 * CpySelect is limited to copy from a single source entity type,
 * specified by sourceEntityName. It is strongly recommended to
 * limit the source result set heavily using sourcesParams.
 * Otherwise, this will become cumbersome for users.
 * For more demanding operations use the CpyDialog.
 */
export function CpySelect({
  value,
  onValueReset,
  destinationId,
  destinationEntityName,
  sourceEntityName,
  sourcesParams,
  placeholder,
  warning = t(
    "Die Auswahl kopiert Daten aus der Auswahl in das akutelle Dokument.",
  ),
  disabled,
  invalid,
  onSelect,
}: CpySelectProps) {
  const { data, isLoading, error } = useCpySourcesQuery({
    destinationEntityName,
    sourceEntityNames: [sourceEntityName],
    params: sourcesParams,
  });

  const [cpy, { isLoading: cpyIsLoading, reset: cpyReset }] =
    useCpyCopyMutation();

  const doCpy = (sourceId: string) => {
    if (cpyIsLoading) return;
    onSelect?.(sourceId);

    cpy({
      destinationEntityName,
      destinationId,
      sourceEntityName,
      sourceId,
    })
      .unwrap()
      .then(() => {
        toast.success(t("Daten kopiert."));
      })
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e));
        cpyReset();
      });
  };
  return (
    <Select
      value={value}
      onValueChange={doCpy}
      disabled={disabled || isLoading || cpyIsLoading}
    >
      <SelectTrigger aria-invalid={invalid || undefined}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {error && <RTKQueryErrorAlert error={error} />}
          {data?.items.map((source) => (
            <SelectItem value={source.id} key={source.id}>
              <span className="flex items-center gap-2">
                {source.name}
                {source.data?.state && (
                  <DocumentStateBadge document={{ state: source.data.state }} />
                )}
              </span>
            </SelectItem>
          ))}
          {data && data.items.length == 0 && (
            <SelectItem value="" disabled>
              {t("Keine Quellen")}
            </SelectItem>
          )}
          {onValueReset && (
            <>
              <SelectSeparator />
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={onValueReset}
              >
                {t("Keine Auswahl")}
              </Button>
            </>
          )}
          {warning !== "" && (
            <>
              <SelectSeparator />
              <SelectLabel>
                <div className="flex items-center gap-2 font-normal">
                  <AlertTriangle className="size-4" /> {warning}
                </div>
              </SelectLabel>
            </>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
